// WEB: `modules/tirvu-armazenamento` → shim `@/platform/tirvu-armazenamento` (VFS em IndexedDB com
// durabilidade "strict": `gravarTextoComFsync` devolve true; a fila antiga não existe no web).
import {
  gravarTextoComFsync,
  lerValorGrandeDoAsyncStorage,
} from "@/platform/tirvu-armazenamento";
import AsyncStorage from "@/platform/async-storage";
import * as FileSystem from "@/platform/file-system";
import { reescreverCaminhosAntigos } from "./CaminhoArquivo";
import type { OfflineAction } from "./Sync";

/**
 * Onde a FILA OFFLINE mora: em ARQUIVO, com um ponteiro pequeno no AsyncStorage.
 *
 * Por quê (05/10/2026): no Android o AsyncStorage (SQLite) grava um valor de qualquer
 * tamanho, mas não o LÊ de volta acima da janela do cursor (~2 MB): o getItem rejeita com
 * "Row too big to fit into CursorWindow" (API 28+) ou "Couldn't read row 0, col 0 from
 * CursorWindow" (API ≤ 27). A fila era UMA chave com todas as ações — e justificativa com
 * anexo, assinatura com selfie e checklist com assinatura levam base64. Passou de 2 MB, o
 * `lerFilaComQuarentena` antigo tomava o erro de leitura por fila corrompida e gravava `[]`
 * por cima: a fila inteira sumia em silêncio. Arquivo não tem esse limite.
 *
 * Como fica:
 *  - cada gravação escreve a fila INTEIRA num arquivo NOVO (`fila_offline/fila_<seq>_<x>.json`,
 *    `seq` crescente — não a hora, que o usuário mexe) e só então troca o ponteiro
 *    (`CHAVE_PONTEIRO`). A troca é uma transação do SQLite: quem lê vê a versão velha ou a
 *    nova, nunca uma pela metade — nem se o app morrer no meio;
 *  - no Android o arquivo é gravado com fsync (modules/tirvu-armazenamento) ANTES da troca:
 *    o SQLite sincroniza o ponteiro com o disco; sem o fsync, um desligamento abrupto logo
 *    depois devolvia o ponteiro certo apontando para um arquivo vazio;
 *  - ficam no disco as 3 versões mais novas. Se a apontada não puder ser lida, vale a
 *    anterior; senão, a legível mais nova da pasta. Só sem nenhuma legível a fila recomeça;
 *  - gravação que falha (disco cheio) apaga o arquivo parcial: ele não pode ficar ocupando
 *    o espaço que as próximas gravações (as baixas do dreno) precisam;
 *  - o ponteiro mora no AsyncStorage. Ponteiro que SUMIU com versões na pasta é perda
 *    involuntária (banco do AsyncStorage apagado, restauração parcial): a fila é a versão
 *    legível mais nova da pasta — nunca uma fila nova por cima. O "Sair" (BottomBar) não
 *    depende mais do clear() para esvaziar a fila: antes dele, a fila é regravada só com o
 *    que precisa sobreviver (`prepararFilaParaSair`, services/Sync.tsx), e é essa versão
 *    que a leitura seguinte encontra (06/10/2026).
 *
 * Regras:
 *  - erro de LEITURA lança. Quem ia regravar desiste e nada é sobrescrito;
 *  - só conteúdo que não é JSON de array vai para a quarentena (em arquivo, inteiro) — e a
 *    fila só recomeça vazia se NENHUMA versão da pasta for legível (senão o dreno ficaria
 *    travado para sempre);
 *  - `lerFilaParaEscrita` e `gravarFila` só dentro do `comFilaLock` (services/Sync.tsx).
 *    Fora dele, só `lerFilaSoLeitura`.
 *
 * Migração (até a 4.0.22 a fila morava em `CHAVE_FILA_LEGADA`): na primeira leitura
 * para escrita, a chave antiga legível vira o primeiro arquivo e é apagada. Ilegível (o
 * valor grande demais) NÃO é tocada: a fila nova começa vazia e a antiga é lida em pedaços
 * pelo módulo nativo (Android, modules/tirvu-armazenamento) e juntada. Sem o nativo, ela
 * continua guardada, e a tentativa se repete.
 */

/** Onde a fila inteira morou até a 4.0.22 (array JSON numa chave só). */
export const CHAVE_FILA_LEGADA = "@TirvuMais:offline_actions";
/** Ponteiro para a versão atual da fila em arquivo. */
const CHAVE_PONTEIRO = "@TirvuMais:fila_offline_arquivo";

// WEB: `documentDirectory` é o do VFS (`file:///tirvu/...`) — mesma estrutura de pastas.
const DIR = `${(FileSystem as any).documentDirectory}fila_offline/`;
const DIR_QUARENTENA = `${DIR}quarentena/`;
/** Só nomes gerados aqui: o ponteiro nunca aponta para fora da pasta. */
const NOME_VERSAO = /^fila_\d+_[a-z0-9]+\.json$/;
/** Versões guardadas no disco (a apontada, a anterior e mais uma de reserva). */
const VERSOES_NO_DISCO = 3;
/** Quantos arquivos de quarentena guardar (os mais novos). */
const MAX_QUARENTENA = 10;
/** Com a fila antiga ilegível, nova tentativa de recuperação a cada 15 min no máximo. */
const INTERVALO_TENTATIVA_LEGADO_MS = 15 * 60 * 1000;

type Ponteiro = {
  /** Versão atual. */
  arquivo: string;
  /** Versão de antes da última troca — reserva se a atual não puder ser lida. */
  anterior?: string;
  /**
   * Estado da fila antiga (`CHAVE_FILA_LEGADA`):
   *  - `migrado`: não existia, ou já foi trazida para o arquivo e não é mais consultada;
   *  - `ilegivel`: existia e não pôde ser lida. Continua guardada, intocada, até ser recuperada.
   */
  legado: "migrado" | "ilegivel";
};

type LeituraPonteiro = Ponteiro | null | "corrompido";

/** Uma leitura da fila e a versão (arquivo) de onde ela saiu — `null` = nenhuma. */
type Leitura = { acoes: OfflineAction[]; nome: string | null };

let ultimaTentativaLegadoEm = 0;

/**
 * Qual versão a última `lerFilaParaEscrita` usou, e sob qual ponteiro. A `gravarFila` da
 * mesma seção do lock registra ESSA como "anterior" — não a apontada, que pode ser a
 * corrompida que a leitura pulou.
 */
let leituraNoLock: { marca: string; nome: string | null } | null = null;
const marcaDoPonteiro = (p: LeituraPonteiro) =>
  p === null ? "#nulo" : p === "corrompido" ? "#corrompido" : p.arquivo;

// WEB: no RN o app é UM processo e o `comFilaLock` (services/Sync.tsx) é um mutex em
// MEMÓRIA — basta para serializar leitura→gravação da fila. No browser, duas ABAS (ou a aba
// e o Service Worker) são dois "processos" sobre o MESMO IndexedDB/arquivos: um mutex em
// memória não enxerga o outro, e as duas gravações intercaladas perdem ações (a de uma aba
// sobrescreve a da outra) ou a faxina de uma apaga a versão que a outra acabou de apontar.
// Proteção com Web Locks (`navigator.locks`), que valem entre abas e workers da mesma origem:
//  1. `comLockFilaEntreAbas` (lock "tirvu:fila") — para o `comFilaLock` do Sync.tsx envolver a
//     seção crítica INTEIRA (lerFilaParaEscrita → … → gravarFila), como o mutex do RN fazia;
//  2. cada operação de ESCRITA daqui (migrar, ler para escrita, gravar) roda sob o lock
//     "tirvu:fila-arquivo": troca de ponteiro + faxina ficam atômicas entre abas mesmo sem (1).
//     Nome diferente de propósito: Web Locks NÃO são reentrantes — com o mesmo nome, (2)
//     dentro de (1) travaria para sempre. Ordem fixa: "tirvu:fila" → "tirvu:fila-arquivo";
//  3. sem (1), uma gravação de OUTRA aba entre a nossa leitura e a nossa gravação é detectada
//     (`gravarFila`) e a gravação LANÇA — quem chama desiste e nada é sobrescrito (a mesma
//     regra do erro de leitura), em vez de apagar em silêncio a ação que a outra aba gravou.
// Sem `navigator.locks` (browser antigo): segue só o mutex em memória, como no RN.
const LOCK_FILA_ENTRE_ABAS = "tirvu:fila";
const LOCK_ARQUIVO_FILA = "tirvu:fila-arquivo";

type GerenciadorDeLocks = {
  request: <T>(nome: string, fn: () => Promise<T>) => Promise<T>;
};

// WEB: `navigator.locks` existe na janela e nos workers (Service Worker do Background Sync).
const gerenciadorDeLocks = (): GerenciadorDeLocks | null => {
  const locks = (globalThis as any)?.navigator?.locks;
  return locks && typeof locks.request === "function"
    ? (locks as GerenciadorDeLocks)
    : null;
};

const comLockWeb = <T>(nome: string, fn: () => Promise<T>): Promise<T> => {
  const locks = gerenciadorDeLocks();
  if (!locks) return fn();
  return locks.request(nome, () => fn());
};

/**
 * WEB: seção crítica da fila entre ABAS/Service Worker (Web Lock "tirvu:fila"). Para o
 * `comFilaLock` (services/Sync.tsx) envolver o corpo dele: `comFilaLock(() =>
 * comLockFilaEntreAbas(fn))`. NÃO é reentrante — nunca chamar de dentro de outra seção com
 * este lock (mesma regra do `comFilaLock`). Sem Web Locks, só executa `fn`.
 */
export function comLockFilaEntreAbas<T>(fn: () => Promise<T>): Promise<T> {
  return comLockWeb(LOCK_FILA_ENTRE_ABAS, fn);
}

/** WEB: a última versão que ESTA aba gravou — para distinguir gravação nossa da de outra aba. */
let ultimaVersaoGravadaAqui: string | null = null;

const parseFila = (texto: string): OfflineAction[] | null => {
  try {
    const v = JSON.parse(texto);
    // Caminhos de foto/anexo de antes de uma atualização (iOS muda a pasta do app a cada
    // update) passam a apontar para a pasta atual — ver services/CaminhoArquivo.ts.
    return Array.isArray(v) ? reescreverCaminhosAntigos(v) : null;
  } catch {
    return null;
  }
};

const seqDe = (nome?: string | null): number => {
  const n = Number(String(nome ?? "").split("_")[1]);
  return Number.isFinite(n) ? n : 0;
};

/** Lê o ponteiro. Lança em erro de leitura; `null` = não existe (nunca migrou, ou "Sair"). */
const lerPonteiro = async (): Promise<LeituraPonteiro> => {
  const bruto = await AsyncStorage.getItem(CHAVE_PONTEIRO);
  if (!bruto) return null;
  try {
    const p = JSON.parse(bruto);
    if (p && typeof p.arquivo === "string" && NOME_VERSAO.test(p.arquivo)) {
      return {
        arquivo: p.arquivo,
        anterior:
          typeof p.anterior === "string" && NOME_VERSAO.test(p.anterior)
            ? p.anterior
            : undefined,
        legado: p.legado === "ilegivel" ? "ilegivel" : "migrado",
      };
    }
  } catch {}
  return "corrompido";
};

type LeituraArquivo =
  | { ok: true; acoes: OfflineAction[] }
  | { ok: false; motivo: "ausente" | "corrompido" };

/** Lê uma versão. Lança em erro de leitura (o arquivo existe e não deu para ler). */
const lerVersao = async (nome: string): Promise<LeituraArquivo> => {
  const caminho = DIR + nome;
  const info = await FileSystem.getInfoAsync(caminho);
  if (!info.exists) return { ok: false, motivo: "ausente" };
  const acoes = parseFila(await FileSystem.readAsStringAsync(caminho));
  return acoes ? { ok: true, acoes } : { ok: false, motivo: "corrompido" };
};

/** Versões na pasta, da mais nova para a mais velha (pela sequência do nome). */
const listarVersoes = async (): Promise<string[]> => {
  const info = await FileSystem.getInfoAsync(DIR);
  if (!info.exists) return [];
  return (await FileSystem.readDirectoryAsync(DIR))
    .filter((n: string) => NOME_VERSAO.test(n))
    .sort((a: string, b: string) => seqDe(b) - seqDe(a));
};

const garantirPasta = async (pasta: string) => {
  const info = await FileSystem.getInfoAsync(pasta);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(pasta, { intermediates: true });
  }
};

const apagar = async (caminho: string) => {
  await FileSystem.deleteAsync(caminho, { idempotent: true }).catch(() => {});
};

/** Mantém só as `MAX_QUARENTENA` cópias mais novas. Nunca lança. */
const podarQuarentena = async () => {
  try {
    const nomes = (await FileSystem.readDirectoryAsync(DIR_QUARENTENA)).sort();
    for (const nome of nomes.slice(0, Math.max(0, nomes.length - MAX_QUARENTENA))) {
      await apagar(DIR_QUARENTENA + nome);
    }
  } catch {}
};

/** Cópia forense de um conteúdo que não é JSON de array. Inteira: arquivo não tem teto. */
const quarentenarTexto = async (texto: string, origem: string) => {
  const destino = `${DIR_QUARENTENA}${Date.now()}_${origem}.json`;
  try {
    await garantirPasta(DIR_QUARENTENA);
    await FileSystem.writeAsStringAsync(destino, texto);
    await podarQuarentena();
  } catch (e) {
    await apagar(destino); // parcial (disco cheio) não fica ocupando espaço
    console.warn("[FilaOffline] Não deu para guardar a cópia forense:", e);
  }
};

/** Tira da pasta uma versão que não é JSON de array (não a apaga). Nunca lança. */
const quarentenarVersao = async (nome?: string) => {
  if (!nome) return;
  try {
    const info = await FileSystem.getInfoAsync(DIR + nome);
    if (!info.exists) return;
    await garantirPasta(DIR_QUARENTENA);
    await FileSystem.moveAsync({
      from: DIR + nome,
      to: `${DIR_QUARENTENA}${Date.now()}_${nome}`,
    });
    await podarQuarentena();
  } catch (e) {
    console.warn("[FilaOffline] Não deu para mover a versão ilegível:", e);
  }
};

/** A versão legível mais nova da pasta, fora as já tentadas. */
const recuperarPelaPasta = async (
  ignorar: (string | undefined)[] = [],
): Promise<Leitura | null> => {
  for (const nome of await listarVersoes()) {
    if (ignorar.includes(nome)) continue;
    const r = await lerVersao(nome);
    if (r.ok) return { acoes: r.acoes, nome };
  }
  return null;
};

/**
 * A fila apontada: a versão atual; ilegível, a anterior; ilegível também, a legível mais
 * nova da pasta (queda de energia: o ponteiro pode apontar para arquivos que voltaram vazios
 * ou nem voltaram, enquanto uma versão mais velha — ou mais nova — está inteira no disco).
 * Nenhuma legível:
 *  - `reparar` (só no lock): quarentena e fila vazia — a próxima gravação cria versão nova;
 *  - senão, lança.
 */
const lerDoPonteiro = async (
  p: Ponteiro,
  reparar: boolean,
): Promise<Leitura> => {
  const ruins: string[] = [];
  let lida: Leitura | null = null;
  for (const nome of [p.arquivo, p.anterior]) {
    if (!nome) continue;
    const r = await lerVersao(nome);
    if (r.ok) {
      lida = { acoes: r.acoes, nome };
      break;
    }
    if (r.motivo === "corrompido") ruins.push(nome);
  }
  if (!lida) lida = await recuperarPelaPasta([p.arquivo, p.anterior]);
  if (lida && lida.nome !== p.arquivo) {
    console.warn(
      "[FilaOffline] Versão apontada ilegível — usando outra versão da pasta:",
      p.arquivo,
      "→",
      lida.nome,
    );
  }
  if (!lida && !reparar) {
    throw new Error("[FilaOffline] Nenhuma versão da fila é legível");
  }
  if (reparar) for (const nome of ruins) await quarentenarVersao(nome);
  if (lida) return lida;
  console.error(
    "[FilaOffline] FILA ILEGÍVEL (nenhuma versão é JSON de array) — em quarentena, fila recomeça vazia:",
    p,
  );
  return { acoes: [], nome: null };
};

/**
 * Escreve uma versão nova e troca o ponteiro. Lança em falha — e aí a fila continua
 * sendo a versão de antes (o ponteiro não mudou). Só no lock.
 *
 * `anterior` = a versão de onde saíram as `acoes` (a que a leitura usou). `cadeiaNova` =
 * não havia ponteiro (primeira gravação, ou depois do "Sair"): o que estiver na pasta não
 * pertence a esta fila e sai na faxina.
 */
const gravarVersao = async (
  acoes: OfflineAction[],
  legado: Ponteiro["legado"],
  anterior: string | null | undefined,
  cadeiaNova: boolean,
): Promise<string> => {
  await garantirPasta(DIR);
  const existentes = await listarVersoes();
  const seq = Math.max(seqDe(existentes[0]), seqDe(anterior)) + 1;
  const nome = `fila_${seq}_${Math.random().toString(36).slice(2, 10) || "0"}.json`;
  const conteudo = JSON.stringify(acoes);
  try {
    // fsync quando o nativo existe (Android, build novo); senão, a gravação comum.
    if (!(await gravarTextoComFsync(DIR + nome, conteudo))) {
      await FileSystem.writeAsStringAsync(DIR + nome, conteudo);
    }
  } catch (e) {
    // Disco cheio deixa o arquivo PELA METADE ocupando o espaço livre — e aí nenhuma
    // gravação seguinte cabe (nem as baixas do dreno, que reenviaria tudo a cada ciclo).
    await apagar(DIR + nome);
    throw e;
  }
  const novo: Ponteiro = {
    arquivo: nome,
    anterior: anterior && anterior !== nome ? anterior : undefined,
    legado,
  };
  try {
    await AsyncStorage.setItem(CHAVE_PONTEIRO, JSON.stringify(novo));
  } catch (e) {
    await apagar(DIR + nome);
    throw e;
  }
  ultimaVersaoGravadaAqui = nome; // WEB: ver `comLockFilaEntreAbas`
  // Faxina: a nova, a anterior e, até completar VERSOES_NO_DISCO, as mais novas. Arquivos de
  // outra cadeia (sem ponteiro: fila zerada pelo "Sair") saem todos.
  try {
    const manter = new Set<string>([nome]);
    if (!cadeiaNova) {
      if (novo.anterior) manter.add(novo.anterior);
      for (const n of existentes) {
        if (manter.size >= VERSOES_NO_DISCO) break;
        manter.add(n);
      }
    }
    for (const n of await listarVersoes()) {
      if (!manter.has(n)) await apagar(DIR + n);
    }
  } catch {}
  return nome;
};

/**
 * Chave de "mesma ação" — a mesma dos dedups do enfileiramento (services/Sync.tsx): uma
 * assinatura por documento/página/empresa, uma batida por `localId`, e o que tem
 * `chave_cliente` (O.S., livro) por tipo + chave. `null` = só o id identifica.
 */
const chaveDeConteudo = (a: any): string | null => {
  const t = String(a?.type ?? "");
  const d = a?.data ?? {};
  const tenant = String(a?.tenantHash || d?.ponto?.hash || d?.hash || "")
    .trim()
    .toUpperCase();
  if (t === "SIGNATURE" && d.id_adm_contra_cheque_colaborador != null) {
    return `${t}|${tenant}|${d.id_adm_contra_cheque_colaborador}:${d.pagina ?? ""}`;
  }
  if (t === "PONTO" && d.localId != null && String(d.localId) !== "") {
    return `${t}|${d.localId}`;
  }
  if (d.chave_cliente) return `${t}|${tenant}|${d.chave_cliente}`;
  return null;
};

/**
 * Ações da fila antiga que ainda não estão na nova — na frente, porque são mais velhas. A
 * nova ganha quando as duas têm a mesma ação (mesmo id ou mesma chave de conteúdo): com a
 * antiga ilegível, a pessoa pode ter refeito a assinatura, e o reconciliador pode ter
 * reenfileirado a batida com outro id. Juntar só por id enviaria as duas.
 */
const juntarComLegado = (
  legado: OfflineAction[],
  atuais: OfflineAction[],
): OfflineAction[] => {
  const ids = new Set(atuais.map((a) => String(a?.id)));
  const chaves = new Set(
    atuais.map(chaveDeConteudo).filter((k): k is string => !!k),
  );
  const antigas = legado.filter((a) => {
    if (!a || typeof a !== "object" || ids.has(String(a.id))) return false;
    const k = chaveDeConteudo(a);
    return !k || !chaves.has(k);
  });
  if (antigas.length < legado.length) {
    console.warn(
      `[FilaOffline] Junção com a fila antiga: ${legado.length - antigas.length} ação(ões) já refeita(s) na nova — fica a nova.`,
    );
  }
  return [...antigas, ...atuais];
};

/**
 * Lê a fila antiga, mesmo grande demais para o getItem.
 * string = valor; null = não existe; undefined = existe e não deu para ler.
 */
const lerFilaLegada = async (): Promise<string | null | undefined> => {
  try {
    return await AsyncStorage.getItem(CHAVE_FILA_LEGADA);
  } catch (e) {
    console.warn(
      "[FilaOffline] Fila antiga não volta pelo getItem (provável valor > 2 MB) — lendo em pedaços:",
      e,
    );
  }
  const emPedacos = await lerValorGrandeDoAsyncStorage(CHAVE_FILA_LEGADA);
  if (typeof emPedacos === "string") {
    console.warn(
      `[FilaOffline] Fila antiga recuperada em pedaços (${emPedacos.length} caracteres).`,
    );
  }
  return emPedacos;
};

/** A fila que o ponteiro (qualquer estado) aponta, reparando se preciso. Só no lock. */
const lerParaEscrita = async (p: LeituraPonteiro): Promise<Leitura> => {
  if (p === null) {
    // Sem ponteiro e com versões na pasta = o ponteiro se perdeu (o "Sair" não depende mais
    // disso). Até 06/10/2026 isso abria uma fila NOVA e a faxina apagava as versões: a fila
    // inteira sumia.
    const rec = await recuperarPelaPasta();
    if (rec) {
      console.warn("[FilaOffline] Ponteiro ausente — fila recuperada da pasta:", rec.nome);
      return rec;
    }
    return { acoes: [], nome: null };
  }
  if (p === "corrompido") {
    const rec = await recuperarPelaPasta();
    if (rec) {
      console.warn("[FilaOffline] Ponteiro ilegível — fila recuperada da pasta:", rec.nome);
    }
    return rec ?? { acoes: [], nome: null };
  }
  const lida = await lerDoPonteiro(p, true);
  if (lida.nome === null) {
    // Reparo: nenhuma versão legível (já em quarentena). O ponteiro passa a apontar para
    // uma fila vazia GRAVADA — senão ele seguia pendurado, toda leitura sem lock lançava e
    // o "Sair" e o logoff remoto ficavam bloqueados até alguém enfileirar algo.
    const nome = await gravarVersao([], p.legado, null, false);
    return { acoes: [], nome };
  }
  return lida;
};

/**
 * Traz a fila antiga para o arquivo, se ainda não foi. Só no lock. Lança em erro de
 * leitura do ponteiro ou da versão atual (nada é gravado).
 */
const migrarSeNecessario = async (): Promise<void> => {
  const p = await lerPonteiro();
  if (p && p !== "corrompido" && p.legado === "migrado") return;
  if (p && p !== "corrompido") {
    // legado "ilegivel": tenta de novo, mas sem ler 2 MB+ a cada acesso à fila.
    if (Date.now() - ultimaTentativaLegadoEm < INTERVALO_TENTATIVA_LEGADO_MS) return;
  }
  ultimaTentativaLegadoEm = Date.now();

  const bruto = await lerFilaLegada();
  if (bruto === null && p === null) return; // nada a migrar; a 1ª gravação cria o ponteiro

  const atual = await lerParaEscrita(p);

  if (bruto === undefined) {
    // Existe e não volta: NÃO toca na chave antiga. A fila nova segue no arquivo e a
    // antiga espera a próxima tentativa.
    console.error(
      "[FilaOffline] FILA ANTIGA ILEGÍVEL — mantida intacta no AsyncStorage; nova tentativa mais tarde.",
    );
    if (p === null || p === "corrompido") {
      await gravarVersao(
        atual.acoes,
        "ilegivel",
        atual.nome,
        p === null && !atual.nome,
      );
    }
    return;
  }

  let legado: OfflineAction[] = [];
  if (bruto !== null) {
    const parsed = parseFila(bruto);
    if (parsed) legado = parsed;
    else {
      console.error(
        "[FilaOffline] Fila antiga não é JSON de array — cópia forense em arquivo.",
      );
      await quarentenarTexto(bruto, "fila_antiga");
    }
  }
  await gravarVersao(
    juntarComLegado(legado, atual.acoes),
    "migrado",
    atual.nome,
    p === null && !atual.nome,
  );
  if (bruto !== null) {
    if (legado.length > 0) {
      console.warn(
        `[FilaOffline] Fila antiga migrada para arquivo: ${legado.length} ação(ões).`,
      );
    }
    // DELETE, sem cursor: funciona mesmo com o valor que não se deixa ler. Se falhar, a
    // chave fica como sobra — com o ponteiro em "migrado", ela não é mais consultada.
    await AsyncStorage.removeItem(CHAVE_FILA_LEGADA).catch(() => {});
  }
};

/**
 * Só a migração da fila antiga, sem ler a fila. Barato depois de feita (uma leitura do
 * ponteiro). Só dentro do `comFilaLock`. Lança em erro de leitura.
 */
export async function migrarFilaAntiga(): Promise<void> {
  // WEB: sob o Web Lock do arquivo da fila (ver `comLockFilaEntreAbas`).
  await comLockWeb(LOCK_ARQUIVO_FILA, () => migrarSeNecessario());
}

/**
 * Fila para quem vai REGRAVÁ-LA. Só dentro do `comFilaLock`.
 * Lança em erro de leitura — quem chama desiste e nada é sobrescrito.
 */
export async function lerFilaParaEscrita(): Promise<OfflineAction[]> {
  // WEB: sob o Web Lock do arquivo da fila (ver `comLockFilaEntreAbas`).
  return comLockWeb(LOCK_ARQUIVO_FILA, async () => {
    await migrarSeNecessario();
    const p = await lerPonteiro();
    const lida = await lerParaEscrita(p);
    leituraNoLock = { marca: marcaDoPonteiro(p), nome: lida.nome };
    return lida.acoes;
  });
}

/**
 * Grava a fila inteira. Só dentro do `comFilaLock`, com `acoes` vindas de
 * `lerFilaParaEscrita` na mesma seção. Lança.
 *
 * ⚠️ NÃO migra: uma migração aqui juntaria a fila antiga numa versão que a gravação logo
 * abaixo substituiria pelas `acoes` de quem chamou, que não a contêm — e a chave antiga já
 * teria sido apagada. Quem migra é a leitura.
 */
export async function gravarFila(
  acoes: OfflineAction[],
  opcoes?: {
    /**
     * Apaga as versões anteriores (cadeia nova). Para o "Sair": as versões velhas guardam
     * ações já entregues da sessão que sai (assinatura com selfie, por exemplo) — e,
     * com o ponteiro perdido, seriam recuperadas na sessão seguinte.
     */
    descartarVersoesAnteriores?: boolean;
  },
): Promise<void> {
  // WEB: sob o Web Lock do arquivo da fila (ver `comLockFilaEntreAbas`).
  return comLockWeb(LOCK_ARQUIVO_FILA, () => gravarFilaNoArquivo(acoes, opcoes));
}

const gravarFilaNoArquivo = async (
  acoes: OfflineAction[],
  opcoes?: { descartarVersoesAnteriores?: boolean },
): Promise<void> => {
  const novaCadeia = !!opcoes?.descartarVersoesAnteriores;
  const p = await lerPonteiro();
  const marca = marcaDoPonteiro(p);
  // WEB: OUTRA aba (ou o Service Worker) trocou o ponteiro entre a nossa leitura e esta
  // gravação — só acontece sem o lock "tirvu:fila" em volta da seção (ver
  // `comLockFilaEntreAbas`). Gravar agora apagaria a ação que ela gravou: lança, e quem chama
  // desiste sem sobrescrever nada (mesma regra do erro de leitura). Versão gravada por esta
  // aba (reparo na leitura, 2ª gravação na mesma seção) não conta.
  if (
    p &&
    p !== "corrompido" &&
    leituraNoLock &&
    leituraNoLock.marca !== marca &&
    marca !== ultimaVersaoGravadaAqui
  ) {
    throw new Error(
      "[FilaOffline] A fila foi alterada em outra aba entre a leitura e a gravação — nada foi sobrescrito",
    );
  }
  // A versão de onde as `acoes` saíram (a leitura pode ter pulado uma apontada corrompida).
  const lidaDe =
    leituraNoLock && leituraNoLock.marca === marca
      ? leituraNoLock.nome
      : p && p !== "corrompido"
        ? p.arquivo
        : null;
  if (p && p !== "corrompido") {
    await gravarVersao(acoes, p.legado, lidaDe, novaCadeia);
    return;
  }
  const legadoExiste = (await AsyncStorage.getAllKeys()).includes(
    CHAVE_FILA_LEGADA,
  );
  if (p === null && legadoExiste) {
    // Sem ponteiro e com a fila antiga: ninguém leu para escrita antes. Gravar marcaria a
    // migração como feita e a fila antiga seria esquecida.
    throw new Error(
      "[FilaOffline] gravarFila antes da migração — leia com lerFilaParaEscrita no mesmo lock",
    );
  }
  // Ponteiro ilegível: sem saber se a fila antiga já veio, ela fica para a próxima leitura.
  // "Cadeia nova" (faxina total) só quando a leitura não aproveitou NENHUMA versão da pasta.
  await gravarVersao(
    acoes,
    legadoExiste ? "ilegivel" : "migrado",
    lidaDe,
    novaCadeia || (p === null && !lidaDe),
  );
};

/**
 * Fila para LER (exibir, contar, decidir). Em qualquer lugar, sem lock e sem efeito
 * colateral (não migra, não repara). Lança em qualquer problema — cada chamador decide o
 * que fazer (exibir vazio, contar como indeterminado…).
 */
export async function lerFilaSoLeitura(): Promise<OfflineAction[]> {
  for (let tentativa = 1; ; tentativa++) {
    const p = await lerPonteiro();
    if (p === null) {
      // Sem ponteiro: a fila antiga (ainda não migrada; lança se for grande demais) e/ou a
      // versão mais nova da pasta (ponteiro perdido) — o mesmo que a leitura no lock adota.
      const bruto = await AsyncStorage.getItem(CHAVE_FILA_LEGADA);
      const daPasta = await recuperarPelaPasta();
      let legado: OfflineAction[] = [];
      if (bruto) {
        const acoes = parseFila(bruto);
        if (!acoes) throw new Error("[FilaOffline] Fila antiga não é JSON de array");
        legado = acoes;
      }
      return daPasta ? juntarComLegado(legado, daPasta.acoes) : legado;
    }
    try {
      if (p === "corrompido") {
        const rec = await recuperarPelaPasta();
        if (rec) return rec.acoes;
        throw new Error("[FilaOffline] Ponteiro e versões ilegíveis");
      }
      return (await lerDoPonteiro(p, false)).acoes;
    } catch (e) {
      // Uma gravação no lock pode ter trocado a versão (e apagado a que íamos ler) entre a
      // leitura do ponteiro e a do arquivo. Ponteiro novo → lê de novo.
      if (tentativa >= 3) throw e;
      const p2 = await lerPonteiro();
      if (marcaDoPonteiro(p2) === marcaDoPonteiro(p)) throw e;
    }
  }
}

/**
 * A fila antiga existia e ainda não pôde ser lida? Há ações guardadas que nenhuma contagem
 * vê. Lança em erro de leitura.
 */
export async function filaAntigaIlegivel(): Promise<boolean> {
  const p = await lerPonteiro();
  return !!p && p !== "corrompido" && p.legado === "ilegivel";
}

/**
 * Existe fila neste aparelho (ponteiro, chave antiga ainda não migrada, ou versão na pasta
 * com o ponteiro perdido)? Lança em erro de leitura.
 */
export async function filaRegistrada(): Promise<boolean> {
  if ((await lerPonteiro()) !== null) return true;
  if ((await AsyncStorage.getAllKeys()).includes(CHAVE_FILA_LEGADA)) return true;
  return (await listarVersoes()).length > 0;
}
